import { rangeDays, type DayRange } from "./dayAxis";

/** Shades of the paid amber for a stack's layers, bottom up, alternating
 *  mid, light, dark and light so neighbours never share a lightness, and
 *  any two at least 26 ΔE (CIE76) apart, so a legend swatch tells them
 *  apart; the bottom one is the nearest to the paid amber, so a month with
 *  one campaign keeps close to the panel's colour. #FCD34D and #F59E0B are
 *  below 3:1 on white, so the legend and the tooltip name every layer. */
export const LAYER_COLORS = ["#B45309", "#FCD34D", "#78350F", "#F59E0B"] as const;
/** "Other" is no campaign of its own, so it recedes: a warm grey. */
export const OTHER_COLOR = "#D6D3D1";
const MAX_NAMED = LAYER_COLORS.length;

export interface StackInput {
  key: string;
  name: string;
  /** "YYYY-MM-DD"; null reads the first daily row. */
  startDate: string | null;
  /** The measure per day, a day without a row left out. */
  daily: ReadonlyArray<{ date: string; value: number }>;
}

export interface StackLayer {
  key: string;
  name: string;
  color: string;
  /** The range's sum, through `through` on a running total. */
  total: number;
  /** "Other" folds more than one campaign. */
  other: boolean;
}

export interface StackRow {
  /** The day's place on the range's axis, from 0. */
  index: number;
  /** "YYYY-MM-DD". */
  date: string;
  /** By layer key; null on a day outside the layer's days, never 0. */
  values: Record<string, number | null>;
  /** The stack's top; null when no layer has the day. */
  total: number | null;
}

export interface CampaignStack {
  /** Bottom first: oldest campaign at the bottom, "Other" on top. */
  layers: StackLayer[];
  /** Each day of the range. */
  rows: StackRow[];
}

interface StackOptions {
  /** The chart's days, which can pass a month's own end. */
  range: DayRange;
  /** A running total by day instead of the day's own value. */
  cumulative?: boolean;
  /** A running total stops here, the source's settled day. */
  through?: string;
}

/** The range's campaigns as layers of a stacked area: the largest four by
 *  total named, oldest at the bottom, the rest folded into "Other" on top.
 *  A day a layer has no row for is null, a gap, never 0 (DD-021). A running
 *  total starts on the layer's first row and, once its campaign ends, keeps
 *  its last value, since the range's total still holds those clicks. Null
 *  when no campaign has a row in the range. */
export const campaignStack = (
  inputs: ReadonlyArray<StackInput>,
  { range, cumulative = false, through = range.until }: StackOptions,
): CampaignStack | null => {
  const lastCounted = cumulative && through < range.until ? through : range.until;
  const campaigns = inputs
    .map((input) => {
      const byDay = new Map<string, number>();
      input.daily.forEach((d) => {
        if (d.date < range.since || d.date > lastCounted) return;
        byDay.set(d.date, (byDay.get(d.date) ?? 0) + d.value);
      });
      const firstDate = Array.from(byDay.keys()).sort()[0] ?? "";
      const total = Array.from(byDay.values()).reduce((a, b) => a + b, 0);
      const start = input.startDate ?? firstDate;
      return { ...input, byDay, firstDate, start, total };
    })
    .filter((c) => c.byDay.size > 0);
  if (campaigns.length === 0) return null;

  const ranked = [...campaigns].sort((a, b) => b.total - a.total || a.start.localeCompare(b.start));
  const named = ranked
    .slice(0, MAX_NAMED)
    .sort((a, b) => a.start.localeCompare(b.start) || a.name.localeCompare(b.name));
  const folded = ranked.slice(MAX_NAMED);

  const groups = [
    ...named.map((c, i) => ({
      layer: { key: c.key, name: c.name, color: LAYER_COLORS[i], total: c.total, other: false },
      members: [c],
    })),
    ...(folded.length > 0
      ? [
          {
            layer: {
              key: "__other",
              name: `Other · ${folded.length} campaign${folded.length === 1 ? "" : "s"}`,
              color: OTHER_COLOR,
              total: folded.reduce((sum, c) => sum + c.total, 0),
              other: true,
            },
            members: folded,
          },
        ]
      : []),
  ];

  const running = new Map<string, number>();
  const rows: StackRow[] = rangeDays(range).map((date, index) => {
    const values: Record<string, number | null> = {};
    groups.forEach(({ layer, members }) => {
      const own = members.filter((m) => m.byDay.has(date));
      const sum = own.reduce((acc, m) => acc + (m.byDay.get(date) ?? 0), 0);
      if (!cumulative) {
        values[layer.key] = own.length > 0 ? sum : null;
        return;
      }
      const started = members.some((m) => m.firstDate <= date);
      const value = (running.get(layer.key) ?? 0) + sum;
      running.set(layer.key, value);
      values[layer.key] = started && date <= through ? value : null;
    });
    const known = Object.values(values).filter((v): v is number => v != null);
    return { index, date, values, total: known.length > 0 ? known.reduce((a, b) => a + b, 0) : null };
  });
  return { layers: groups.map((g) => g.layer), rows };
};

/** The rows a stacked chart draws: where a layer starts or stops between
 *  two days, both days repeat once with only the layers they share, so the
 *  layers above step on a vertical edge at the day instead of sloping
 *  across to the next one. The repeats keep their day, so a tooltip reads
 *  the day's own row; a layer missing a day stays null, never 0 (DD-021). */
export const stackEdges = (rows: ReadonlyArray<StackRow>): StackRow[] =>
  rows.flatMap((row, i) => {
    const next = rows[i + 1];
    const keys = Object.keys(row.values);
    const has = (r: StackRow, key: string) => r.values[key] != null;
    if (!next || keys.every((key) => has(row, key) === has(next, key))) return [row];
    const shared = (r: StackRow): StackRow => {
      const values = Object.fromEntries(
        keys.map((key) => [key, has(row, key) && has(next, key) ? r.values[key] : null]),
      );
      const known = Object.values(values).filter((v): v is number => v != null);
      return { ...r, values, total: known.length > 0 ? known.reduce((a, b) => a + b, 0) : null };
    };
    return [row, shared(row), shared(next)];
  });
